import logger from './logger';

// Shared channel name across tabs
const CHANNEL_NAME = 'auth-sync';

/**
 * Cross-tab auth broadcaster using the BroadcastChannel API.
 *
 * Browser support (as of 2025):
 * - Chrome ≥ 54, Firefox ≥ 38, Edge ≥ 79, Safari ≥ 15.4 (macOS & iOS)
 *
 * Docs:
 * - MDN: https://developer.mozilla.org/en-US/docs/Web/API/Broadcast_Channel_API#browser_compatibility
 */
export function createAuthBroadcaster() {
  if (typeof BroadcastChannel === 'undefined') {
    logger.warn('BroadcastChannel not supported; auth sync will be disabled.');
    return {
      emit: () => {},
      listen: () => () => {},
    };
  }

  const channel = new BroadcastChannel(CHANNEL_NAME);

  /**
   * Emit a cross-tab event.
   * @param {string} type - e.g., "login" or "logout"
   * @param {any} payload - optional data (e.g., user object)
   */
  const emit = (type, payload = null) => {
    const msg = { type, payload, ts: Date.now() };

    try {
      channel.postMessage(msg);
    } catch (err) {
      logger.warn('Auth broadcast failed:', err);
    }
  };

  /**
   * Start listening for cross-tab events.
   * @param {(msg: {type: string, payload: any, ts: number}) => void} callback
   * @returns {() => void} unsubscribe cleanup function
   */
  const listen = (callback) => {
    const onMessage = (e) => callback(e.data);
    channel.addEventListener('message', onMessage);

    // Return an unsubscribe to clean up listeners when unmounting
    return () => {
      channel.removeEventListener('message', onMessage);
      channel.close();
    };
  };

  return { emit, listen };
}
